import { Injector } from '@angular/core';
// savings-balance.validator.ts
import { AbstractControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  CustomValidators,
  SavingsBalanceRule,
  SavingsBalanceValidatorConfig,
} from '@finxone-platform/shared/sys-config-types';
import { Store } from '@ngxs/store';
import Handlebars from 'handlebars';
import { AvailableSavingsProductType } from '../../services/savings-account-service/savings-account.type';
import { SavingsProductsState } from '@app/finxone-web-frontend/app/lib/state/savings-products.state';

export class SavingsBalanceValidator {
  constructor(private injector: Injector) {}

  createSyncFromConfig(config: SavingsBalanceValidatorConfig): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      // Skip validation if control is empty and configured to do so
      if (config.skipValidationOnEmpty && !control.value) {
        return null;
      }

      // Get the product code from the specified field
      // This needs to search up to the root form group and down to the target control
      // For example, if the control is nested within multiple form groups
      const root = control.root; // Navigate to root
      // search in child form groups recursively
      const findControlRecursively = (group: AbstractControl, path: string): AbstractControl | null => {
        const control = group.get(path);
        if (control) {
          return control;
        }

        if (!(group instanceof FormGroup)) {
          return null;
        }

        // Search in child form groups
        const childGroups = Object.values(group.controls).filter(
          (ctrl) => ctrl instanceof FormGroup,
        ) as FormGroup[];

        for (const childGroup of childGroups) {
          const foundControl = findControlRecursively(childGroup, path);
          if (foundControl) {
            return foundControl;
          }
        }

        return null;
      };
      // Find the control that holds the product code
      const foundControl = findControlRecursively(root, config.productCodeField);
      if (!foundControl || !foundControl.value) {
        return null; // Can't validate without product code
      }

      const amount = parseFloat(control.value);
      if (isNaN(amount)) {
        return null;
      }

      const store = this.injector.get(Store);
      const product = store.selectSnapshot(SavingsProductsState.getProduct(foundControl.value));

      if (!product) {
        return null; // Product not found, skip validation
      }

      // Find matching rules
      const matchingRule = this.findMatchingRule(product, config.rules);

      if (!matchingRule) {
        return null; // No rules apply
      }

      // Apply validation
      const errors = this.validateAmount(amount, matchingRule.validation, product);
      return errors;
    };
  }

  /**
   * Finds the highest priority matching rule
   */
  private findMatchingRule(
    product: AvailableSavingsProductType,
    rules: SavingsBalanceRule[],
  ): SavingsBalanceRule | null {
    const matchingRules = rules.filter((rule) => this.ruleMatches(product, rule));

    if (matchingRules.length === 0) {
      return null;
    }

    // Sort by priority (lower number = higher priority)
    return matchingRules.sort((a, b) => (a.priority || 999) - (b.priority || 999))[0];
  }

  /**
   * Checks if a rule matches the product
   */
  private ruleMatches(product: AvailableSavingsProductType, rule: SavingsBalanceRule): boolean {
    const { conditions } = rule;
    const operator = conditions.operator || 'OR';

    if (
      conditions.productCode === undefined &&
      conditions.productType === undefined &&
      conditions.termMonthsRange === undefined
    ) {
      // No conditions specified, matches all products
      return true;
    }

    const codeMatches = conditions.productCode ? conditions.productCode.includes(product.productCode) : null;

    const typeMatches = conditions.productType ? conditions.productType.includes(product.accountType) : null;

    const termMonthsRange = conditions.termMonthsRange || [null, null];

    let termMatches: boolean | null = null;
    if (termMonthsRange[0] !== null || termMonthsRange[1] !== null) {
      const term = product.termMonths || 0;
      const minTerm = termMonthsRange[0] !== null ? termMonthsRange[0] : Number.NEGATIVE_INFINITY;
      const maxTerm = termMonthsRange[1] !== null ? termMonthsRange[1] : Number.POSITIVE_INFINITY;
      termMatches = term >= minTerm && term <= maxTerm;
    }

    if (operator === 'AND') {
      // All conditions must be true (or not specified)
      return (
        (codeMatches === null || codeMatches) &&
        (typeMatches === null || typeMatches) &&
        (termMatches === null || termMatches)
      );
    } else {
      // At least one condition must be true
      return codeMatches === true || typeMatches === true || termMatches === true;
    }
  }

  /**
   * Validates amount against rule requirements
   */
  private validateAmount(
    amount: number,
    validation: SavingsBalanceRule['validation'],
    product: AvailableSavingsProductType,
  ): ValidationErrors | null {
    const errors: ValidationErrors = {};

    if (validation.minAmount && amount < validation.minAmount) {
      if (validation.minErrorMessage)
        errors['minAmount'] = Handlebars.compile(validation.minErrorMessage)({
          minAmount: this.formatCurrency(validation.minAmount),
          product: product,
        });
      else errors['minAmount'] = `Minimum deposit required is ${this.formatCurrency(validation.minAmount)}`;
    }

    if (validation.maxAmount && amount > validation.maxAmount) {
      if (validation.maxErrorMessage)
        errors['maxAmount'] = Handlebars.compile(validation.maxErrorMessage)({
          maxAmount: this.formatCurrency(validation.maxAmount),
          product: product,
        });
      else errors['maxAmount'] = `Minimum deposit required is ${this.formatCurrency(validation.maxAmount)}`;
    }

    if (product.minBalance && amount < parseFloat(product.minBalance)) {
      if (validation.minErrorMessage)
        errors['minAmount'] = Handlebars.compile(validation.minErrorMessage)({
          product: product,
        });
      else
        errors['minAmount'] = `Minimum deposit allowed is ${this.formatCurrency(
          parseFloat(product.minBalance),
        )}`;
    }

    if (product.maxDeposit && amount > parseFloat(product.maxDeposit)) {
      if (validation.maxErrorMessage)
        errors['maxAmount'] = Handlebars.compile(validation.maxErrorMessage)({
          product: product,
        });
      else
        errors['maxAmount'] = `Maximum deposit allowed is ${this.formatCurrency(
          parseFloat(product.maxDeposit),
        )}`;
    }

    return Object.keys(errors).length > 0 ? errors : null;
  }

  private formatCurrency(amount: number, currency = 'GBP'): string {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
      maximumFractionDigits: amount % 1 === 0 ? 0 : 2,
    }).format(amount);
  }
}

// validation-config.json (example configuration)
export const SAVINGS_VALIDATION_CONFIG: SavingsBalanceValidatorConfig = {
  type: CustomValidators.SAVINGS_BALANCE_VALIDATOR,
  productCodeField: 'productCode',
  skipValidationOnEmpty: false,
  defaultErrorMessage: 'Please enter a valid deposit amount for this product',
  rules: [
    {
      conditions: {
        productCode: ['310'],
        operator: 'OR',
      },
      validation: {
        minAmount: 5000,
        maxAmount: 500000,
        minErrorMessage: 'Premium Savings Account requires a minimum deposit of £5,000',
      },
      priority: 1,
    },
    {
      conditions: {
        productType: ['fixed-term'],
        operator: 'OR',
      },
      validation: {
        minAmount: 1000,
        maxAmount: 100000,
        minErrorMessage: 'Fixed-term accounts require a minimum deposit of £1,000',
      },
      priority: 2,
    },
    {
      conditions: {
        productType: ['easy-access'],
        operator: 'OR',
      },
      validation: {
        minAmount: 1,
        maxAmount: 50000,
        minErrorMessage: 'Easy Access accounts require a minimum deposit of £1',
      },
      priority: 3,
    },
    {
      conditions: {
        termMonthsRange: [12, 24],
      },
      validation: {
        minAmount: 150,
        minErrorMessage: 'Accounts with a term between 12 and 24 months require a minimum deposit of £150',
      },
      priority: 4,
    },
    {
      conditions: {
        productCode: ['800', '510'],
        productType: ['easy-access'],
        operator: 'AND',
      },
      validation: {
        minAmount: 10,
        minErrorMessage: 'This Easy Access account requires a minimum deposit of £10',
      },
      priority: 0,
    },
  ],
};
